//************************************************************************ /
// RSVP LIST CARD
// White card in the user nav menu that lists the 5 most recent RSVPs for the event,
// with the pagination option to view the next/previous 5.
//************************************************************************ /

import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Avatar,
  NextAndPreviousBar,
  message,
} from "@dreambigger/design-system/src/components";
import { eventApi } from "@dreambigger/shared/src/api/weserve/api-helper";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";
import { DonationRecords } from "@dreambigger/shared/src/api/events";
import getInitials from "@dreambigger/shared/src/utils/get-initials";

const PAGE_SIZE = 3;

type Props = {
  eventId: number;
};

const LatestDonations = ({ eventId }: Props) => {
  const [donations, setDonations] = useState<DonationRecords["records"]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState<number>();

  useEffect(() => {
    eventApi
      .getDonors(
        eventId,
        generateWeserveQueryParams({
          offset: String(offset),
          limit: String(PAGE_SIZE),
          sort: "created_at",
          direction: "desc",
        })
      )
      .then((res) => {
        setDonations(res.data.records);
        setTotalRecords(res.data.totalRecords);
      })
      .catch(() => message.error("Something went wrong loading the Donations"))
      .finally(() => {
        setLoading(false);
      });
  }, [offset, eventId]);

  const getPreviousThree = useCallback(() => {
    setLoading(true);

    setOffset((oldOffset) => {
      if (oldOffset < PAGE_SIZE) {
        // We don't want to return a negative offset, so just return 0.
        return 0;
      }
      return oldOffset - PAGE_SIZE;
    });
  }, [offset]);

  const getNextThree = useCallback(() => {
    setLoading(true);

    setOffset((oldOffset) => {
      return oldOffset + PAGE_SIZE;
    });
  }, [offset]);

  //   Conditionally render "No donations yet" message when donations are empty
  if (donations.length === 0) {
    return (
      <div className="fwb flex justify-center lh-3">
        <div>
          No donations yet.
          {/* ADD LINK TO DONATIONS */}
          <a
            className="flex underline"
            style={{
              color: "#243984",
            }}
            href=""
            target="_blank"
          >
            Be the first to donate!
          </a>
        </div>
      </div>
    );
  }

  // If there are donations, render the list
  return (
    <div>
      {donations.map((item) => (
        <div
          style={{
            boxShadow: "0 2px 4px -1px #e8e8e8",
          }}
          className="bg-white ba b-gray-4 br-1"
          key={item.id}
        >
          <div className="row pa-3 flex items-center">
            {item.isPublic && (
              <div>
                <Avatar size="large" alt="story user avatar">
                  {getInitials(item?.user?.name).toUpperCase()}
                </Avatar>
              </div>
            )}
            <div className="pl-2 flex w-100">
              <h4
                style={{
                  display: item.isPublic ? "flex" : "none",
                }}
                className="fwb mb-0 pl-0"
              >
                {item.isPublic ? item.user.name : "Anonymous"}{" "}
                {item.hasOrganization ? item.orgName : ""}{" "}
                {item.isAmountPublic
                  ? `donated $${item.totalDonated}`
                  : "made a donation"}
              </h4>
              <h4
                style={{
                  display: !item.isPublic ? "flex" : "none",
                }}
                className="fwb mb-0 pl-0"
              >
                Anonymous{" "}
                {item.isAmountPublic
                  ? `donated $${item.totalDonated}`
                  : "made a donation"}
              </h4>
            </div>
          </div>
          {/* donation comment */}
          <div
            style={{
              display: item.comment && item.comment !== "-" ? "flex" : "none",
            }}
          >
            <div className="pl-4 pb-4 gray-9 f-3 lh-1">
              &ldquo;{item.comment}&rdquo;
            </div>
          </div>
        </div>
      ))}
      {totalRecords && totalRecords > PAGE_SIZE && (
        <div
          style={{
            boxShadow: "0 2px 4px -1px #e8e8e8",
          }}
          className="bg-white ba b-gray-4 br-1"
        >
          <NextAndPreviousBar
            loading={loading}
            className="pa-3"
            actionTextClass="primary ttu fwb f-2 hlight-black"
            previousText="Previous Three"
            nextText="Next Three"
            showLeft={offset !== 0}
            showRight={totalRecords > offset + PAGE_SIZE}
            nextFunction={getNextThree}
            previousFunction={getPreviousThree}
          />
        </div>
      )}
    </div>
  );
};

export default memo(LatestDonations);
